import React, { useState, useEffect } from "react";
import "./clients.css";
import API_URL from "../Config";
import axiosInstance from "../axiosinstance.js";
import PaginationFooter from "../Components/PaginationFooter.js";
import { IoIosAdd } from "react-icons/io";
import { FiSearch } from "react-icons/fi";

const Clients = ({ isSidebarExpanded }) => {
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [newClient, setNewClient] = useState({
    name: "",
    location: "",
    code: "",
    timezone: "",
  });

  // Fetch all clients on component mount
  useEffect(() => {
    fetchClients("");
  }, []);

  // Fetch clients when page changes
  useEffect(() => {
    fetchClients(searchQuery);
  }, [currentPage, itemsPerPage]);

  // Debounced search - fetch after user stops typing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(1); // Reset to first page on new search
      fetchClients(searchQuery);
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Fetch clients with search query
  const fetchClients = async (query = "") => {
    setLoading(true);
    setError(null);
    try {
      const authKey = localStorage.getItem("authKey");
      const url = `${API_URL}/api/get_client_list${
        query ? `?query=${encodeURIComponent(query)}` : ""
      }`;
      const res = await axiosInstance.get(url, {
        headers: { Authorization: `Bearer ${authKey}` },
      });

      const data = res.data;
      setClients(data.data || []);
      setTotalRecords(data.total_records || data.data?.length || 0);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to fetch clients";
      setError(errorMessage);
      console.error("Error fetching clients:", err);
      setClients([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
  };

  // Handle create client
  const handleCreateClient = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const authKey = localStorage.getItem("authKey");
      const url = `${API_URL}/api/create_client`;
      const res = await axiosInstance.post(url, newClient, {
        headers: { Authorization: `Bearer ${authKey}` },
      });

      const data = res.data;

      // Reset form and close modal
      setNewClient({
        name: "",
        location: "",
        code: "",
        timezone: "",
      });
      setShowCreateModal(false);

      // Refresh client list
      await fetchClients(searchQuery);

      // Show success message
      alert(data.message || "Client created successfully");
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to create client";
      setError(errorMessage);
      console.error("Error creating client:", err);
      alert(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle input change for new client form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewClient((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <>
      <div
        className={`table-container clients-page ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
        style={{ display: "flex", flexDirection: "column" }}
      >
        <div className="clients-page-content">
          <div className="clients-page-header">
            <div className="clients-header-titleblock">
              <h2 className="page-title mb-0">Clients</h2>
            </div>
            <div className="clients-header-buttons">
              <div className="clients-header-controls-row">
                <div className="clients-search-inline">
                  <FiSearch className="clients-search-icon" />
                  <input
                    type="text"
                    placeholder="Search clients by name, location, or code..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                </div>
                <button
                  className="clients-btn-create"
                  onClick={() => setShowCreateModal(true)}
                >
                  <IoIosAdd /> <span>Create Client</span>
                </button>
              </div>
            </div>
          </div>

          {error && <div className="clients-error-message">Error: {error}</div>}

          <div className="clients-table-card">
            <div className="clients-table-wrapper">
              <table className="clients-data-table">
                <colgroup>
                  <col className="clients-col-name" />
                  <col className="clients-col-code" />
                  <col className="clients-col-location" />
                  <col className="clients-col-timezone" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Client Name</th>
                    <th>Code</th>
                    <th>Location</th>
                    <th>Timezone</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 10 }).map((_, rowIdx) => (
                      <tr key={`clients-skeleton-${rowIdx}`}>
                        {Array.from({ length: 4 }).map((__, colIdx) => (
                          <td key={colIdx}>
                            <div
                              style={{
                                height: "10px",
                                background: "#e5e8ee",
                                borderRadius: "3px",
                                animation: "pulse 2s ease-in-out infinite",
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : clients.length > 0 ? (
                    clients.map((client) => (
                      <tr key={client.id}>
                        <td>
                          <span className="clients-name">
                            {client.client_name}
                          </span>
                        </td>
                        <td>{client.code}</td>
                        <td>{client.location}</td>
                        <td>{client.timezone}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="clients-no-data">
                        No clients found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <PaginationFooter
              totalRecords={totalRecords}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              setCurrentPage={setCurrentPage}
              setItemsPerPage={setItemsPerPage}
            />
          </div>
        </div>
      </div>

      {/* Create Client Modal */}
      {showCreateModal && (
        <div
          className="clients-modal-overlay"
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="clients-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="clients-modal-header">
              <h2>Create New Client</h2>
              <button
                className="clients-btn-close"
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateClient}>
              <div className="clients-form-group">
                <label htmlFor="name">Client Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={newClient.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter the client name"
                />
              </div>

              <div className="clients-form-group">
                <label htmlFor="code">Client Code</label>
                <input
                  type="text"
                  id="code"
                  name="code"
                  value={newClient.code}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter the client code (eg. ABCD001)"
                />
              </div>

              <div className="clients-form-group">
                <label htmlFor="location">Location</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={newClient.location}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter the client location"
                />
              </div>

              <div className="clients-form-group">
                <label htmlFor="timezone">Timezone</label>
                <select
                  id="timezone"
                  name="timezone"
                  value={newClient.timezone}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Timezone</option>
                  <option value="Asia/Kolkata">Asia/Kolkata</option>
                  <option value="Asia/Jerusalem">Asia/Jerusalem</option>
                  <option value="America/New_York">America/New_York</option>
                  <option value="America/Los_Angeles">
                    America/Los_Angeles
                  </option>
                  <option value="Europe/London">Europe/London</option>
                  <option value="Europe/Paris">Europe/Paris</option>
                  <option value="Asia/Tokyo">Asia/Tokyo</option>
                  <option value="Asia/Dubai">Asia/Dubai</option>
                  <option value="Australia/Sydney">Australia/Sydney</option>
                </select>
              </div>

              <div className="clients-modal-footer">
                <button
                  type="button"
                  className="clients-btn-cancel"
                  onClick={() => setShowCreateModal(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="clients-btn-submit"
                  disabled={loading}
                >
                  {loading ? "Creating..." : "Create Client"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Clients;
